/**
 * Horas extra del empleado.
 *
 * Puede reportar y corregir **mientras no estén aprobadas**. Una vez aprobadas
 * son un dato de planilla y la pantalla ya no ofrece editar — la regla está en
 * el servidor, pero mostrar un botón que va a fallar sería mentirle a la gente.
 *
 * En escritorio (B5) el formulario deja de ser un botón que abre modal: va
 * INLINE a la izquierda, con el total del mes debajo, y el historial a la
 * derecha (`.msec--split`). En el teléfono sigue el botón + modal de siempre
 * — el mismo formulario, montado en un lugar u otro según `useMediaQuery`.
 */

import { useState, type ChangeEvent, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { dateLabel, today, OVERTIME_STATUS_LABEL, OVERTIME_STATUS_TONE, type OvertimeStatus } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Button, Empty, Field, KeyValueRow, Modal, Money, Note } from '@/components/ui';
import { useMediaQuery } from './useMediaQuery';

interface Entry {
  id: string; work_date: string; start_time: string | null; end_time: string | null;
  hours: number; reason: string; status: OvertimeStatus; amount_minor: number | null;
  unit_code: string | null; rejection_reason: string | null;
}

/* Las duraciones que de verdad se reportan. El caso raro se escribe a mano. */
const HORAS_COMUNES = ['1', '1.5', '2', '3', '4'] as const;

type ReportFormState = { work_date: string; hours: string; reason: string };
const FORM_VACIO: ReportFormState = { work_date: today(), hours: '', reason: '' };

/**
 * El estado y el envío del formulario, compartidos entre la tarjeta inline
 * de escritorio y el modal del teléfono: la única diferencia entre las dos
 * es dónde vive el botón de enviar, no la lógica de qué se manda.
 */
function useReportForm(onSuccess: () => void) {
  const [form, setForm] = useState<ReportFormState>(FORM_VACIO);

  const mutation = useApiMutation(
    () => api.post('/employee/overtime', {
      work_date: form.work_date,
      hours: form.hours,
      reason: form.reason,
    }),
    ['/employee/overtime', '/employee/home'],
    { onSuccess: () => { setForm(FORM_VACIO); onSuccess(); } },
  );

  const set = (k: keyof ReportFormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid = form.hours !== '' && form.reason.length >= 3;

  return { form, setForm, set, mutation, valid };
}

export function EmployeeOvertime() {
  const query = useApi<{ overtime: Entry[] }>('/employee/overtime');
  const [reporting, setReporting] = useState(false);
  const desktop = useMediaQuery('(min-width: 901px)');

  return (
    <>
      <div className="msec--split pantalla-split">
        <div className="pantalla-split__main">
          <section className="msec">
            <div className="msec__title">Reportar horas</div>
            {desktop ? (
              <ReportarInline />
            ) : (
              <div className="mcard">
                <Button variant="primary" block onClick={() => setReporting(true)}>
                  Reportar horas
                </Button>
              </div>
            )}
          </section>

          {/* El total del mes es un dato de escritorio: en el teléfono el
              historial ya lo tiene a la vista con un scroll corto. Sin un
              segundo `<Async>`: la columna de al lado ya cubre la carga y el
              error de esta misma consulta, y duplicarlos aquí solo pondría
              dos «Cargando…» a la vez la primera vez que entra alguien. */}
          {desktop && query.data && <TotalDelMes entries={query.data.overtime} />}
        </div>

        <div className="pantalla-split__lateral msec--split-lateral">
          <section className="msec">
            <div className="msec__title">Mis horas extra</div>
            <Async query={query} empty={(d) => d.overtime.length === 0 && (
              <Empty icon="horas" title="Todavía no reportaste horas extra" />
            )}>
              {(data) => (
                <>
                  {data.overtime.map((o) => (
                    <div key={o.id} className="mcard">
                      <div className="mrow" style={{ paddingTop: 0 }}>
                        <span>
                          <strong>{dateLabel(o.work_date)}</strong>
                          <span className="msub">
                            {o.hours} horas{o.unit_code ? ` · ${o.unit_code}` : ''}
                          </span>
                        </span>
                        <Badge tone={OVERTIME_STATUS_TONE[o.status]}>{OVERTIME_STATUS_LABEL[o.status]}</Badge>
                      </div>
                      <KeyValueRow label={o.reason} style={{ borderBottom: o.rejection_reason ? undefined : 0 }}>
                        {o.amount_minor != null ? <Money value={o.amount_minor} /> : '—'}
                      </KeyValueRow>
                      {o.rejection_reason && (
                        <Note tone="danger">{o.rejection_reason}</Note>
                      )}
                    </div>
                  ))}
                </>
              )}
            </Async>
          </section>
        </div>
      </div>

      {reporting && <ReportModal onClose={() => setReporting(false)} />}
    </>
  );
}

/** Cuánto se reportó (y cuánto vale) en lo que va del mes, a la vista sin entrar
 *  al historial. Solo aparece si hay algo que contar. */
function TotalDelMes({ entries }: { entries: Entry[] }) {
  const mes = today().slice(0, 7);
  const delMes = entries.filter((o) => o.work_date.startsWith(mes));
  if (delMes.length === 0) return null;

  const horas = delMes.reduce((sum, o) => sum + o.hours, 0);
  const monto = delMes.reduce((sum, o) => sum + (o.amount_minor ?? 0), 0);

  return (
    <section className="msec">
      <div className="mcard horas-total">
        <div className="horas-total__cifra">
          <strong>{Number.isInteger(horas) ? horas : horas.toFixed(1)} h</strong>
          <span>reportadas este mes</span>
        </div>
        {monto > 0 && (
          <div className="horas-total__cifra">
            <strong><Money value={monto} /></strong>
            <span>este mes</span>
          </div>
        )}
      </div>
    </section>
  );
}

/** Los tres campos, sin `<form>` propio: cada lugar que los usa decide cómo
 *  envuelve el envío (inline el mismo formulario, el modal con el botón en
 *  el pie). */
function CamposDeReporte({ form, set, setForm }: {
  form: ReportFormState;
  set: (k: keyof ReportFormState) => (e: ChangeEvent<HTMLInputElement>) => void;
  setForm: Dispatch<SetStateAction<ReportFormState>>;
}) {
  return (
    <>
      <Field label="Día" required>
        <input className="input" type="date" max={today()} value={form.work_date}
          onChange={set('work_date')} />
      </Field>

      <Field label="¿Cuántas horas?" required>
        <div className="chip-opciones chips-horas">
          {HORAS_COMUNES.map((h) => (
            <button
              key={h}
              type="button"
              aria-pressed={form.hours === h}
              className={`chip-opcion${form.hours === h ? ' is-on' : ''}`}
              onClick={() => setForm((f) => ({ ...f, hours: h }))}
            >
              {h.replace('.5', '½')}
            </button>
          ))}
        </div>
        <input className="input input--money" type="number" step="0.25" min="0.25" max="24"
          placeholder="U otra cantidad" value={form.hours} onChange={set('hours')} />
      </Field>

      <Field label="Motivo" required>
        <input className="input" value={form.reason} onChange={set('reason')} />
      </Field>
    </>
  );
}

/** La tarjeta de escritorio: el mismo formulario, sin modal ni pie propio —
 *  el botón de enviar es parte del `<form>`, no un pie separado. */
function ReportarInline() {
  const { form, setForm, set, mutation, valid } = useReportForm(() => {});

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (valid && !mutation.isPending) mutation.mutate();
  }

  return (
    <form className="mcard horas-form" onSubmit={onSubmit}>
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && <Note tone="ok">Reportado. Queda pendiente de aprobación.</Note>}
      <CamposDeReporte form={form} set={set} setForm={setForm} />
      <Button type="submit" variant="primary" block disabled={!valid || mutation.isPending}>
        {mutation.isPending ? 'Enviando…' : 'Reportar'}
      </Button>
    </form>
  );
}

const MODAL_FORM_ID = 'reporte-horas-form';

/** El modal del teléfono. Envuelve los campos en un `<form>` propio: Enter
 *  en cualquier campo lo manda, y el botón del pie se asocia por `form=` sin
 *  vivir dentro del `<form>` (el pie del `Modal` del sistema es un hermano
 *  del cuerpo, no un hijo). */
function ReportModal({ onClose }: { onClose: () => void }) {
  const { form, setForm, set, mutation, valid } = useReportForm(onClose);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (valid && !mutation.isPending) mutation.mutate();
  }

  return (
    <Modal
      title="Reportar horas extra"
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" form={MODAL_FORM_ID} variant="primary" disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Enviando…' : 'Reportar'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <form id={MODAL_FORM_ID} onSubmit={onSubmit}>
        <CamposDeReporte form={form} set={set} setForm={setForm} />
      </form>
    </Modal>
  );
}
